'use server'

import { apiRequest, ApiResponse } from '../request'

export type Recurrence = 'MONTHLY' | 'YEARLY' | 'SEMIANNUAL'

export interface Plan {
    id: number
    name: string
    description: string
    price: number
    isCustom: boolean
    customPrice?: number | null
    private: boolean
    recurrence: Recurrence
    /**
     * Período grátis sugerido, em dias, ao criar um link deste plano. Quem
     * concede de fato é o link — este valor só preenche o formulário.
     */
    trialDays?: number | null
    features: any
    tierId?: number | null
    tier?: { id: number; name: string } | null
    additionalModules: Record<string, string[]>
    excludedModules: string[]
    createdAt: string
    updatedAt: string
}

export interface CreatePlanData {
    name: string
    description: string
    price: number
    isCustom?: boolean
    customPrice?: number
    private?: boolean
    recurrence?: Recurrence
    trialDays?: number | null
    features: any
    tierId?: number | null
    additionalModules?: Record<string, string[]>
    excludedModules?: string[]
}

export interface UpdatePlanData {
    name?: string
    description?: string
    price?: number
    isCustom?: boolean
    customPrice?: number | null
    private?: boolean
    recurrence?: Recurrence
    trialDays?: number | null
    features?: any
    tierId?: number | null
    additionalModules?: Record<string, string[]>
    excludedModules?: string[]
}

export async function getPlans(
    includePrivate?: boolean,
): Promise<ApiResponse<Plan[]>> {
    const params = includePrivate ? { includePrivate: 'true' } : {}
    return apiRequest<Plan[]>('plans', { params })
}

export async function getPlan(id: number): Promise<ApiResponse<Plan>> {
    return apiRequest<Plan>(`plans/${id}`, {})
}

export async function createPlan(
    data: CreatePlanData,
): Promise<ApiResponse<Plan>> {
    return apiRequest<Plan>('plans', { data }, 'post')
}

export async function updatePlan(
    id: number,
    data: UpdatePlanData,
): Promise<ApiResponse<Plan>> {
    return apiRequest<Plan>(`plans/${id}`, { data }, 'patch')
}

export async function deletePlan(id: number): Promise<ApiResponse<Plan>> {
    return apiRequest<Plan>(`plans/${id}`, {}, 'delete')
}

export type RegistrationLinkStatus = 'ACTIVE' | 'USED' | 'REVOKED' | 'EXPIRED'

export interface RegistrationLink {
    id: number
    planId: number
    token: string
    label: string | null
    status: RegistrationLinkStatus
    expiresAt: string | null
    /** `null` = uso ilimitado. */
    maxUses: number | null
    usedCount: number
    lastUsedAt: string | null
    /** Dias de período grátis concedidos por este link. `null` = nenhum. */
    trialDays: number | null
    /** Contas nascidas deste link (as 20 mais recentes). */
    usedByCompanies: Array<{ id: number; name: string | null }>
    revokedAt: string | null
    createdAt: string
}

export interface CreateRegistrationLinkData {
    label?: string
    expiresAt?: string
    /** true = link de campanha, aceita quantos cadastros vierem. */
    unlimited?: boolean
    /**
     * Dias de período grátis para quem se cadastrar por este link. Ausente =
     * usa o padrão do plano; 0 = sem período grátis, mesmo com padrão definido.
     */
    trialDays?: number
}

/**
 * Links de cadastro do plano (apenas MASTER). Um plano pode ter vários links
 * vivos ao mesmo tempo; cada um vale por um cadastro, ou por quantos vierem
 * quando criado como ilimitado.
 */
export async function getPlanRegistrationLinks(
    id: number
): Promise<ApiResponse<RegistrationLink[]>> {
    return apiRequest<RegistrationLink[]>(`plans/${id}/registration-links`, {})
}

/**
 * Cria mais um link. Os links já distribuídos continuam valendo.
 */
export async function createPlanRegistrationLink(
    id: number,
    data: CreateRegistrationLinkData = {}
): Promise<ApiResponse<RegistrationLink>> {
    return apiRequest<RegistrationLink>(
        `plans/${id}/registration-links`,
        { data },
        'post'
    )
}

/** Invalida um link específico, sem afetar os demais (apenas MASTER). */
export async function revokePlanRegistrationLink(
    id: number,
    linkId: number
): Promise<ApiResponse<RegistrationLink>> {
    return apiRequest<RegistrationLink>(
        `plans/${id}/registration-links/${linkId}/revoke`,
        {},
        'post'
    )
}

export const REGISTRATION_LINK_STATUS_LABELS: Record<
    RegistrationLinkStatus,
    string
> = {
    ACTIVE: 'Ativo',
    USED: 'Esgotado',
    REVOKED: 'Revogado',
    EXPIRED: 'Expirado',
}

export const RECURRENCE_LABELS: Record<Recurrence, string> = {
    MONTHLY: 'Mensal',
    YEARLY: 'Anual',
    SEMIANNUAL: 'Semestral',
}

export const formatPlanPrice = (centavos: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(centavos / 100)
}

const PLAN_CIPHER_KEY = 'tz_atende_plan_k3y'

export function encodePlanId(id: number): string {
    const str = `plan_${id}_${id * 7 + 13}`
    let encoded = ''
    for (let i = 0; i < str.length; i++) {
        const charCode =
            str.charCodeAt(i) ^
            PLAN_CIPHER_KEY.charCodeAt(i % PLAN_CIPHER_KEY.length)
        encoded += String.fromCharCode(charCode)
    }
    return btoa(encoded)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
}

export function decodePlanId(encoded: string): number | null {
    try {
        let b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
        while (b64.length % 4) b64 += '='
        const decoded = atob(b64)
        let str = ''
        for (let i = 0; i < decoded.length; i++) {
            const charCode =
                decoded.charCodeAt(i) ^
                PLAN_CIPHER_KEY.charCodeAt(i % PLAN_CIPHER_KEY.length)
            str += String.fromCharCode(charCode)
        }
        const match = str.match(/^plan_(\d+)_(\d+)$/)
        if (!match) return null
        const id = parseInt(match[1], 10)
        const checksum = parseInt(match[2], 10)
        if (checksum !== id * 7 + 13) return null
        return id
    } catch {
        return null
    }
}
